import { Injectable, Injector } from "@angular/core";
import { Observable } from "rxjs";
import { JwtHttpClientService } from "../http/jwt-http-client.service";
import { TranslateService, DefaultTranslateParser } from "ng2-translate";
/**
 * Service for retrieving i18n messages from the server.
 *
 * Errors are returned as a #DataClientErrorMessage
 */
export class I18nService extends DefaultTranslateParser {
    /**
     * Constructs the service with a Http service, and Injector service
     * @param http HTTP Service
     * @param injector The angular injector
     */
    constructor(http, injector) {
        super();
        this.http = http;
        this.injector = injector;
        /*
         * Track whether we are loading / have loaded server-side translations for a given language
         */
        this.serverTranslationsLoading = {};
    }
    /**
     * Handles a missing translation.
     * If the server translations have not been loaded, attempts to reload them for both the current and default language, and then retries the translation.
     * Note that once the default translation has been loaded, ng2-translate will always default to the default translations, so if the current language were to change,
     * the new server-side translations would never be loaded via this handler.  For now, it is assumed that we will not be changing languages on the fly - if this changes,
     * the server-side translations will have to be loaded at the time of switching.
     * @param params
     * @return an observable of the translated value (or the untranslated key if server-side translationshave already been loaded)
     */
    handle(params) {
        if (this.serverTranslationsLoaded(params.translateService, params.translateService.currentLang)) {
            return this.getDefaultTranslation(params.key);
        }
        let load;
        if (params.translateService.currentLang === params.translateService.getDefaultLang()) {
            load = this.loadServerTranslations(params.translateService.currentLang);
        }
        else {
            load = Observable.merge(this.loadServerTranslations(params.translateService.currentLang), this.loadServerTranslations(params.translateService.getDefaultLang()));
        }
        return load
            .last()
            .flatMap(() => params.translateService.get(params.key, params.interpolateParams))
            .catch(() => Observable.of(this.getDefaultTranslation(params.key)));
    }
    getDefaultTranslation(key) {
        return key.startsWith("'") && key.endsWith("'") ? key.substring(1, key.length - 1) : "[" + key + "]";
    }
    /**
     * Loads and merges the server-side i18n translations for the specified language.
     * @param language The language code for which to retrieve the translations
     * @return An observable of a single boolean (always true) indicating that the server translations are loaded
     */
    loadServerTranslations(language) {
        if (this.serverTranslationsLoaded(this.getTranslateService(), language)) {
            return Observable.of(true);
        }
        if (this.serverTranslationsLoading[language]) {
            return this.serverTranslationsLoading[language];
        }
        return this.serverTranslationsLoading[language] = this.http.getWithoutToken(I18nService.URL + language)
            .map((response) => response.json())
            .catch((error) => {
            console.log("Failed to obtain server-side translations for '" + language + "': " + error.toString());
            this.serverTranslationsLoading[language] = undefined;
            return Observable.throw(error);
        })
            .map((translations) => {
            this.getTranslateService().setTranslation(language, translations, true);
            this.serverTranslationsLoading[language] = undefined;
            return true;
        })
            .share();
    }
    interpolate(expr, params) {
        if (typeof expr !== 'string') {
            return expr;
        }
        return expr.replace(this.templateMatcher, (substring, paramKey) => {
            if (params) {
                let paramValue = this.getValue(params, paramKey);
                if (typeof paramValue !== 'undefined' && paramValue !== null) {
                    if (typeof paramValue === 'string' && paramValue.indexOf("{{") >= 0) {
                        paramValue = this.interpolate(paramValue, params);
                    }
                    return paramValue;
                }
            }
            try {
                return this.getTranslateService().instant(paramKey, params);
            }
            catch (err) {
                return paramKey;
            }
        });
    }
    serverTranslationsLoaded(translateService, language) {
        const langTranslations = translateService["translations"][language];
        return langTranslations && langTranslations.server;
    }
    /**
     * Obtain the translate service from the injector directly to avoid a provider cyclic dependency
     */
    getTranslateService() {
        if (!this.translate) {
            this.translate = this.injector.get(TranslateService);
        }
        return this.translate;
    }
}
/*
 * Static URL prefix for all server side calls
 */
I18nService.URL = '/dataserver/i18n/';
I18nService.decorators = [
    { type: Injectable },
];
/** @nocollapse */
I18nService.ctorParameters = () => [
    { type: JwtHttpClientService, },
    { type: Injector, },
];
//# sourceMappingURL=i18n.service.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@fedelta-pos/data-client/i18n/i18n.service.js
// module id = 218
// module chunks = 1